import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  PageTitle,
  Text,
  Button,
  Input,
  Grid,
  Select,
  Spacer,
  FlexiPagination,
} from "@flexisaf/flexibull2";

import usePapersGet from "../../hooks/papers/usePapersGet";
import { deletePaperStart, setCurrentPaper } from "../../redux/paper/reducer";
import { PaperCardList } from "../../components/CardList";
import { setCurrentExamGroup } from "../../redux/exam-group/reducer";
import { setCurrentExam } from "../../redux/exam/reducer";

import PaperEditor from "../paper/modals/paperEditor";
import Theme from "../../theme";
import { useSelector } from "react-redux";
import { selectAllExamGroups } from "../../redux/exam-group/selectors";
import { EmptyState } from "@flexisaf/flexibull2/build/emptystates";
import { Loader } from "@flexisaf/flexibull2/build/loader";
import { selectExamList } from "../../redux/exam/selectors";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import { DefaultCurriculum } from "../../utils/constants";

const Papers = () => {
  const [modal, setModal] = useState({
    show: false,
    data: null,
  });
  const [deletePaperModal, setDeletePaperModal] = useState({
    openModal: false,
    data: null,
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const exams = useSelector(selectExamList);
  const {
    papers,
    loadingPapers,
    refetch,
    limit,
    setLimit,
    totalElements,
    page,
    setPage,
    pageOptions,
    searchText,
    setSearchText,
    setExamGroupId,
    examGroupId,
    setExamId,
    examId,
    setCurriculumType,
    curriculumType,
  } = usePapersGet({ id: "" }); //getting all papers
  const examGroups = useSelector(selectAllExamGroups);

  useEffect(() => {
    dispatch(setCurrentExamGroup(null));
    dispatch(setCurrentExam(null));
  }, [dispatch]);

  const onEdit = (data) => {
    setModal({
      show: true,
      data,
    });
  };

  const onDelete = (data) => {
    let payload = {
      id: data.id,
      callback: () => {
        refetch();
      },
    };
    dispatch(deletePaperStart(payload));
  };

  return (
    <Box>
      <PageTitle>
        <i className="saf-stickynote" />
        <Text
          bold
          title="Papers"
          size={Theme.H3}
          block={false}
          style={{ padding: "0 10px" }}
        >
          Papers
        </Text>
      </PageTitle>

      <Grid
        default="minmax(auto, 300px) minmax(auto, 150px) minmax(auto, 150px) auto max-content"
        gap="24px"
      >
        <Input
          name="search"
          block
          placeholder="Search papers"
          iconLeft="saf-search-normal"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Box>
          <Select
            block
            placeholder="Select curriculum type"
            options={DefaultCurriculum?.map((item) => {
              return {
                label: item.label,
                value: item.value,
              };
            })}
            value={curriculumType}
            onChange={(value) => setCurriculumType(value)}
          />
        </Box>
        <Box>
          <Select
            block
            placeholder="Exam Group"
            onChange={(e) => setExamGroupId({ ...examGroupId, value: e.value })}
            options={examGroups?.items?.map((x) => {
              return { label: x.name, value: x.id };
            })}
          />
        </Box>
        <Box>
          <Select
            block
            placeholder="Exam"
            isLoading={loadingPapers}
            options={exams?.items?.map((x) => {
              return {
                label: x.name,
                value: x.id,
              };
            })}
            onChange={(e) => setExamId({ ...examId, value: e.value })}
            disable={examGroupId.value === "" ? true : false}
          />
        </Box>
        <Box />

        <Button
          onClick={() => {
            setModal({
              show: true,
              data: null,
            });
          }}
        >
          Create Paper
        </Button>
      </Grid>
      <Spacer space="40px" />
      <>
        {loadingPapers ? (
          <Box pad="150px 0">
            <Loader color={Theme.PrimaryColor} section height="70vh" />
          </Box>
        ) : papers?.items?.length > 0 ? (
          <>
            <Grid default="1fr 1fr 1fr 1fr" gap="20px">
              {papers?.items?.map((paper) => {
                return (
                  <PaperCardList
                    key={paper.id}
                    handleRedirect={() => {
                      dispatch(setCurrentPaper(paper));
                      navigate(`/papers/${paper.id}`);
                    }}
                    handleEdit={() => onEdit(paper)}
                    handleDelete={() => {
                      setDeletePaperModal({ openModal: true, data: paper });
                    }}
                    paperData={paper}
                    questionCount="20"
                  />
                );
              })}
            </Grid>
            <FlexiPagination
              pageCounts={pageOptions}
              itemsDisplayed
              total={totalElements}
              pageSize={limit}
              onChange={(page) => {
                setPage(page);
              }}
              changePageSize={({ value }) => {
                setPage(1);
                setLimit(value);
              }}
              current={page}
            />
          </>
        ) : (
          <Box align="center">
            <EmptyState
              type="documents"
              title="Paper not found."
              info="Please create paper to proceed"
            />
          </Box>
        )}
        {modal?.show && (
          <PaperEditor
            open={modal.show}
            data={modal.data}
            cbProps={{ refetchPaper: false, refetchPapers: refetch }}
            closeModal={() => {
              setModal({
                show: false,
                data: null,
              });
              document.body.style.overflow = "auto";
            }}
          />
        )}
        <DeleteModal
          isOpen={deletePaperModal.openModal}
          onClose={() => setDeletePaperModal({ openModal: false, data: null })}
          title="Delete Paper"
          description="The execution of this action will result in the removal of the paper and all of its associated questions from the system."
          isLoading={loadingPapers}
          onDelete={() => {
            onDelete(deletePaperModal?.data);
            setDeletePaperModal({ openModal: false, data: null });
          }}
        />
      </>
    </Box>
  );
};

export default Papers;
